//@ts-strict-ignore
import { Injectable, ViewContainerRef } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, tap } from 'rxjs/operators';
import { HttpClient, HttpParams } from '@angular/common/http';
import {LMBusyIndicatorService, LMContextService, LMErrorUtilService, LMLoggerService, LMSpecService, LMUrlUtilService} from '@app/services';
import { HttpParamEncoderService } from '@services/utils/http.param.encoder.service';
import {
  LMReport,
  LMReportCategoriesByModule,
  LMReportDefListSpec,
  LMReportSearchValues,
  LMReportSortColumn,
  LMReportsResponse
} from '../lm-reports';
import { KeyedResource, ListResource } from '@app/resources';
import { AppConstants } from '@app/utils';
import { LMListSpecUtilService, LMPrintCsvInfo, LMPrintPdfInfo} from '@app/components';

@Injectable()
export class LMReportsCatalogService {
  private favoritesStringArray: Array<string>;
  private deliveredReportsArray: LMReport[];
  private reportsArray: LMReport[] = [];
  private categoriesByModule: LMReportCategoriesByModule = {};

  private reportsArraySource = new BehaviorSubject<LMReport[]>(this.reportsArray);
  private categoriesByModuleSource = new BehaviorSubject<LMReportCategoriesByModule>(this.categoriesByModule);
  private reportSearchResultsSource = new BehaviorSubject<LMApplicationMenuReportMenu[]>([]);

  public reportsArray$ = this.reportsArraySource.asObservable();
  public categoriesByModule$ = this.categoriesByModuleSource.asObservable();
  public reportSearchResults$ = this.reportSearchResultsSource.asObservable();

  private reportsSpec: LMReportsResponse;
  private activeTab: string;
  private allReportsSearchValues: LMReportSearchValues;
  private reportsSortColumn: LMReportSortColumn;

  private reportDefListSpecObj: LMReportDefListSpec;

  constructor(
    private http: HttpClient,
    private lmContextService: LMContextService,
    private lmLoggerService: LMLoggerService,
    private lmUrlUtilService: LMUrlUtilService,
    private lmSpecService: LMSpecService,
    private lmListSpecUtilService: LMListSpecUtilService,
    private lmErrorUtilService: LMErrorUtilService,
    private lmBusyIndicatorService: LMBusyIndicatorService
  ) {}

  public getReportsCatalogData(): Observable<any> {
    let url = this.lmUrlUtilService.getReportUrl('catalog');

    const params: LMUrlParams = {
      output: 'spec',
      requestType: 'reportCatalog',
      includeDelivered: true
    };

    if (this.deliveredReportsArray) {
      params.includeDelivered = false;
    }

    url = this.lmUrlUtilService.updateUrlParams(url, params);

    return this.makeHttpCall(url, params, null).pipe(
      catchError(err=> {
        this.lmLoggerService.error(err);
        const { message } = err.error;
        this.lmErrorUtilService.handleError(message);
        this.lmBusyIndicatorService.hideBusyIndicator();
        return EMPTY;
      }),
      tap((response: LMReportsResponse) => {
        if (response.status === 'SUCCESS') {
          this.processReportsCatalogData(response);
        } else {
          this.lmErrorUtilService.handleUnexpectedError(response);
        }
      })
    );
  }

  private processReportsCatalogData(catalogData: LMReportsResponse): void {
    this.reportsArray = [ ...catalogData.reportsJSONArray];

    if (!this.favoritesStringArray) {
      this.favoritesStringArray = catalogData.favoritesStringArray;
    }

    // apply cached delivered reports
    if (this.deliveredReportsArray) {
      this.reportsArray = this.reportsArray.concat(this.deliveredReportsArray);
    } else {
      this.deliveredReportsArray = this.reportsArray.filter(report => report.isDelivered);
    }

    this.reportsArraySource.next(this.reportsArray);

    this.categoriesByModule = this.buildCategoriesByModule(this.reportsArray);
    this.categoriesByModuleSource.next(this.categoriesByModule);
  }

  public searchReports(reportName = ''): void {
    if (this.lmContextService.disableReportSearch()) {
      this.reportSearchResultsSource.next([]);
      return;
    }
    // search report from the cache
    if (this.reportsArray.length) {
      let reportSearchResults: LMApplicationMenuReportMenu[] = [];

      if (reportName.length) {
        let searchFunc: (...args: unknown[]) => boolean;

        if (reportName.startsWith('*')) {
          searchFunc = "".startsWith;
          reportName = reportName.substring(1);
        } else if (reportName.endsWith('*')) {
          searchFunc = "".endsWith;
          reportName = reportName.substring(0, reportName.length - 1);
        } else {
          searchFunc = "".includes;
        }

        // if report catalog is enabled, search includes delivered reports
        if (this.lmContextService.getContext().enableReportCatalog) {
          reportSearchResults = this.reportsArray
            .filter(report => searchFunc.call(report.title.toLowerCase(), reportName.toLowerCase()))
            .map(({ id, title, url }) => ({ automationId: id.replace('.', '-'), id, name: title, type: 'Reports', url }));
        } else {
          // else search only user folder reports
          reportSearchResults = this.reportsArray
            .filter(report => !report.isDelivered && searchFunc.call(report.title.toLowerCase(), reportName.toLowerCase()))
            .map(({ id, title, url }) => ({ automationId: id.replace('.', '-'), id, name: title, type: 'Reports', url }));
        }

        if (reportName.length <= 2) {
          // if the search string has only 2 chars, return the first five elements only
          reportSearchResults = [ ...reportSearchResults.slice(0, 5) ];
        }

        reportSearchResults = [ ...reportSearchResults.sort((a, b) => a.name.localeCompare(b.name)) ];
      }

      this.reportSearchResultsSource.next(reportSearchResults);
    } else {
      // search report from the backend
      const url = this.lmUrlUtilService.getReportUrl('catalog');

      let searchType = 'contains';

      if (reportName.startsWith('*')) {
        searchType = 'startsWith';
        reportName = reportName.substring(1);
      } else if (reportName.endsWith('*')) {
        searchType = 'endsWith';
        reportName = reportName.substring(0, reportName.length - 1);
      }

      const params = {
        requestType: 'searchReports',
        includeDelivered: !!this.lmContextService.getContext().enableReportCatalog,
        searchType,
        searchText: reportName,
        limitSearch: reportName.length >= 2
      };

      this.makeHttpCall(url, params, null).subscribe(
        (searchReportsResponse) => {
          let reportSearchResults: LMApplicationMenuReportMenu[] = searchReportsResponse?.reportsJSONArray || [];
          reportSearchResults = [ ...reportSearchResults.sort((a, b) => a.name.localeCompare(b.name)) ];
          this.reportSearchResultsSource.next(reportSearchResults);
        }
      );

      // background fetch catalog data
      this.getReportsCatalogData().pipe(distinctUntilChanged()).subscribe();
    }
  }

  public getFavorites():Array<string> {
    return this.favoritesStringArray || [];
  }

  public updateFavorites(report: LMReport)  {
    const isExistingFavorite = report.isFavorite;

    if (isExistingFavorite && this.favoritesStringArray.indexOf(report.id) !== -1) { // Remove from Favorites
      const favoritesIndex = this.favoritesStringArray.indexOf(report.id);
      this.favoritesStringArray.splice(favoritesIndex, 1);
    } else if (!isExistingFavorite && this.favoritesStringArray.indexOf(report.id) === -1) { // Add to Favorites
      if (Array.isArray(this.favoritesStringArray)) {
        this.favoritesStringArray.push(report.id);
      } else {
        this.favoritesStringArray = [ report.id ];
      }
    } else {
      this.lmLoggerService.log('Warning: problem in Report Catalog > onFavoriteClicked with report: ' + report.id);
    }
  }

  public getReportDefListSpecObj(): LMReportDefListSpec {
    return this.reportDefListSpecObj || null;
  }

  public setReportDefListSpecObj(reportDefListSpecObj: LMReportDefListSpec) {
    this.reportDefListSpecObj = reportDefListSpecObj;
  }

  public getReportUrl(report: LMReport): Observable<any> {
    let url = this.lmUrlUtilService.getReportUrl('catalog');

    const params = {
      requestType: 'reportUrl',
      catalogId: report.id
    };

    url = this.lmUrlUtilService.updateUrlParams(url, params);

    return this.makeHttpCall(url, params, null);
  }

  public visitReport(report: LMReport): Observable<any> {
    let url = this.lmUrlUtilService.getReportUrl('catalog');

    const params = {
      requestType: 'reportUrl',
      catalogId: report.id
    };

    url = this.lmUrlUtilService.updateUrlParams(url, params);

    return this.makeHttpCall(url, params, null);
  }

  private buildCategoriesByModule(reportsArray: LMReport[]): LMReportCategoriesByModule {
    const categoriesByModule: LMReportCategoriesByModule = {};
    const categoriesByModuleSet = reportsArray.reduce((result: { [module: string]: Set<string | LMAppReportOption> }, currentValue: LMReport) => {
      result[currentValue['module']] = (result[currentValue['module']] || new Set()).add(currentValue['category']);
      return result;
    }, {});

    Object.keys(categoriesByModuleSet)?.forEach(key => {
      categoriesByModule[key] = Array.from(categoriesByModuleSet[key].values()).sort();
    });

    return categoriesByModule;
  }

  public removeReport(report: LMReport) {
    const index = this.reportsArray.findIndex((r) => r.id === report.id);

    if (index > -1) {
      this.reportsArray.splice(index, 1);
      this.reportsArray = [ ...this.reportsArray ];
    }

    this.reportsArraySource.next(this.reportsArray);
    this.categoriesByModule = this.buildCategoriesByModule(this.reportsArray);
    this.categoriesByModuleSource.next(this.categoriesByModule);
  }

  public getActiveTab() {
    return this.activeTab;
  }

  public setActiveTab(activeTab: any) {
    this.activeTab = activeTab;
  }

  public getAllReportsSearchValues() {
    return this.allReportsSearchValues;
  }

  public setAllReportsSearchValues(allReportsSearchValues: any) {
    this.allReportsSearchValues = allReportsSearchValues;
  }

  public getStoredCatalogSpec() {
    return this.reportsSpec;
  }

  public setStoredCatalogSpec(spec: any) {
    this.reportsSpec = spec;
  }

  public buildReportUrl(report: LMReport): string {
    let urlParams: LMUrlParams = { ...{ output: 'spec' }, ...this.lmContextService.getContext().contextSessionKeys } as LMUrlParams;
    if (report.owner != '') {
      urlParams = { ...urlParams, ...{ userfolder: report.id } };
    }
    return LMUrlUtilService.buildUrl2(this.lmUrlUtilService.getBaseUrl(), report.type.toLowerCase(), report.resourceId, undefined, urlParams);
  }

  public getListSpec(resourceId: string): Observable<any> {
    const urlParams: LMUrlParams = this.lmContextService.getContext().contextSessionKeys;
    const url = LMUrlUtilService.buildUrl2(this.lmUrlUtilService.getBaseUrl(), 'list', resourceId, null, urlParams);
    return this.lmSpecService.getListSpec(url);
  }

  public buildCatalogNavigationUrl(extraParams?: LMUrlParams) {
    // Pass in { panel: 'favorites' | 'all' | 'printfiles' }
    let urlParams: LMUrlParams = { ...{ requestType: 'reportCatalog' }, ...this.lmContextService.getContext().contextSessionKeys } as LMUrlParams;
    if (extraParams) {
      urlParams = { ...urlParams, ...extraParams };
    }
    return LMUrlUtilService.buildUrl2(this.lmUrlUtilService.getBaseUrl(), 'report', 'catalog', null, urlParams);
  }

  public deleteUserFolderReport(reportId: string): Observable<any> {
    const postContent = { /*action: '_execute',*/ requestType: 'deleteUserFolderReport', reportId };
    const urlParams: LMUrlParams = { ...{ requestType: 'deleteUserFolderReport', reportId }, ...this.lmContextService.getContext().contextSessionKeys } as LMUrlParams;
    const url = LMUrlUtilService.buildUrl2(this.lmUrlUtilService.getBaseUrl(), 'report', 'catalog', null, urlParams);
    return this.makeHttpCall(url, postContent, null);
  }

  public getReportListType(report: LMReport) {
    const postContent = {
      requestType: 'getReportListType',
      businessClass: report.busClass,
      listName: report.name,
      userFolder: report.id
    };

    const urlParams: LMUrlParams = { ...{ requestType: 'getReportListType'}, ...this.lmContextService.getContext().contextSessionKeys } as LMUrlParams;
    const url = LMUrlUtilService.buildUrl2(this.lmUrlUtilService.getBaseUrl(), 'report', 'catalog', null, urlParams);
    return this.makeHttpCall(url, postContent, null);
  }

  public getReportDefinitionRecordDataView(report: LMReport): Observable<any> {
    const searchParams = {
      sortOrderName: 'ReportDefinition.ByReportName',
      pageop: 'load',
      isAscending: true,
      pagesize: 5,
    };
    const urlParams: LMUrlParams = { ...searchParams, ...this.lmContextService.getContext().contextSessionKeys } as LMUrlParams;
    const url = LMUrlUtilService.buildUrl2(this.lmUrlUtilService.getBaseUrl(), 'list', 'ReportDefinition.ReportCatalogDataList', undefined, urlParams);
    let requestParams;
    if (report.isDelivered) {
      if (report.type.toUpperCase() === 'PAGE') {
        requestParams = {
          'fc.ReportDefinition_prd_BusinessView': 'equals(' + report.resourceId + ')',
        };
      } else {
        requestParams = {
          'fc.ReportDefinition_prd_BusinessView': 'equals(' + report.busClass + ')',
          'fc.ReportDefinition_prd_Name': 'equals(' + report.name + ')',
          'fc.ReportDefinition_prd_UserFolder': 'isEmpty()',
        };
      }

    } else {
      requestParams = {
        'fc.ReportDefinition_prd_UserFolder': 'equals(' + report.id + ')',
      };
    }

    return this.makeHttpCall(url, null, requestParams);
  }

  private makeHttpCall(url: string, postContent?: any, requestParams?: any): Observable<any> {
    let params = new HttpParams({ encoder: new HttpParamEncoderService() });
    if (requestParams) {
      Object.keys(requestParams).forEach((key: string) => (params = params.set(key, requestParams[key])));
    }
    if (postContent) {
      return <Observable<any>>this.http.post(url, postContent, { params }).pipe(
        catchError((error) => {
          this.lmLoggerService.error(error);
          return throwError(error || 'Server error');
        })
      );
    } else {
      return <Observable<any>>this.http.get(url, { params }).pipe(
        catchError((error) => {
          this.lmLoggerService.error(error);
          return throwError(error || 'Server error');
        })
      );
    }
  }

  public buildListCsvSettings(report: LMReport, spec: LMListSpec, listUrlInfo: LMListUrlInfo, modalPlaceholder: ViewContainerRef) {
    const actionReference = {
      automationId: 'StandardExportToCSV',
      id: 'StandardExportToCSV',
      label: 'Export To CSV',
      type: 'standard',
    };
    const listResourceImpl = new ListResource({ resourceId: spec.resourceId });
    const settings: LMPrintCsvInfo = <LMPrintCsvInfo>{
      modalPlaceholder,
      resource: listResourceImpl,
      fromResource: this.buildListFromResource(listUrlInfo),
      fromList: listUrlInfo.fromListResource,
      actionReference,
      csvType: this.getListCSVType(spec.busListSpec),
      name: report.title,
      dependentPanels: null,
      cubeOverrides: undefined,
      cubeOptions: undefined,
      userFolder: spec.userFolder,
      selectedRows: [],
      filterFields: [],
      scheduleActionAllowed: spec.scheduleActionAllowed,
      includeRecordLimit: true,
      isTreeList: spec.busListSpec.implementsTreeView,
      hasDetailSections: this.lmListSpecUtilService.hasDetailSections(spec),
      singleRecordSelected: false,
      dataViewSet: undefined,
      selectRequestData: undefined,
      selectedDetailPanelIndex: undefined,
      retentionDuration: spec.retentionDuration,
    };
    return settings;
  }

  private buildListFromResource(listUrlInfo: LMListUrlInfo): KeyedResource {
    let fromResource: KeyedResource;

    if (listUrlInfo.sourceResource) {
      fromResource = new KeyedResource({
        resourceId: listUrlInfo.sourceResource.resourceId,
        resourceType: listUrlInfo.sourceResource.resourceType,
      });
    }
    return fromResource;
  }

  private getListCSVType(busListSpec: LMBusListSpec): string {
    if (busListSpec.implementsTreeView) {
      return busListSpec.detailSections ? AppConstants.TREE_LIST_WITH_DETAILS_CSV : AppConstants.TREE_LIST_CSV;
    }
    return AppConstants.REGULAR_LIST_CSV;
  }

  public buildListPdfSettings(report: LMReport, spec: LMListSpec, listUrlInfo: LMListUrlInfo) {
    const actionReference = {
      automationId: 'StandardPrintToFile',
      id: 'StandardPrintToFile',
      label: 'Print To PDF',
      type: 'standard',
    };
    const pdfType = this.getListPDFType(spec.busListSpec);
    const listResourceImpl = new ListResource({ resourceId: spec.resourceId });
    const settings = <LMPrintPdfInfo>{
      resource: listResourceImpl,
      fromResource: this.buildListFromResource(listUrlInfo),
      fromList: listUrlInfo.fromListResource,
      actionReference,
      name: report.title,
      pdfType,
      includeSearchForm: false,
      cubeOverrides: undefined,
      cubeOptions: undefined,
      userFolder: spec.userFolder,
      fontOffset: spec.pdfFontOffset || spec.busListSpec.fontOffset,
      orientation: spec.pdfOrientation || spec.busListSpec.orientation,
      dataViewSet: undefined,
      selectedRows: undefined,
      filterFields: undefined,
      scheduleActionAllowed: spec.scheduleActionAllowed,
      includeRecordLimit: true,
      selectRequestData: undefined,
      retentionDuration: spec.retentionDuration,
      paperSize: spec.pdfPaperSize,
      customHeight: spec.pdfCustomHeight,
      customWidth: spec.pdfCustomWidth,
      customUnit: spec.pdfCustomUnit
    };
    return settings;
  }
  public getListPDFType(busListSpec: LMBusListSpec): string {
    if (busListSpec.implementsTreeView) {
      return busListSpec.detailSections ? AppConstants.TREE_LIST_WITH_DETAILS_PDF : AppConstants.TREE_LIST_PDF;
    }
    if (busListSpec.detailSections || busListSpec.showChangedValuesOnRight) {
      return AppConstants.LIST_WITH_DETAILS_PDF;
    }
    return AppConstants.REGULAR_LIST_PDF;
  }

  public getReportsSortColumn() {
    return this.reportsSortColumn;
  }
  public setReportsSortColumn(columnId: string, ascending: boolean) {
    this.reportsSortColumn = {
      columnId,
      ascending,
    };
  }
}
